import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, cn, Icon, Skeleton } from "@nexus/ui";
import { ADMIN_DASH, ADMIN_LABEL_CLASS } from "./admin-layout-tokens";

/** How a metric reads when it is carrying something an operator should act on. */
export type AdminKpiTone = "default" | "error" | "warning";

export interface AdminKpiMetric {
  id: string;
  /** Rendered in the one uppercase label style. Never carries weight. */
  label: string;
  /**
   * `null` renders an em dash. A metric that has no answer says so — a bare
   * zero would claim one.
   */
  value: ReactNode | null;
  /**
   * A present value that should not draw the eye — a zero, a free window, a
   * figure no rate covers. Healthy is quiet, so a real number is the thing an
   * operator's eye lands on.
   */
  isMuted?: boolean;
  /** One job: the exact figure, the caveat, or the companion percentile. */
  sub?: ReactNode;
  /** The precise value the compact one abbreviates. */
  title?: string;
  /**
   * Paired with an icon by the caller wherever it matters — a tone alone is
   * colour, and colour alone carries nothing.
   */
  tone?: AdminKpiTone;
  /** Narrows the page to what this metric counts. Makes the metric a button. */
  onFilter?: () => void;
  /** True while the page is already narrowed to it, so the metric is the way out. */
  isFilterActive?: boolean;
  /** Why the filter is unavailable, and what the metric means when it is. */
  filterTitle?: string;
}

interface AdminKpiStripProps {
  /** Three to six. The seventh belongs in `live` or in a breakdown panel. */
  metrics: AdminKpiMetric[];
  /**
   * The one metric pinned to the right edge: a live gauge rather than a total
   * over the window, so it reads as measuring something else.
   */
  live?: AdminKpiMetric;
  isLoading: boolean;
  /** These numbers belong to the previous filter; the next one is in flight. */
  isStale: boolean;
  isError?: boolean;
  onRetry?: () => void;
}

const VALUE_CLASS = "text-lg font-semibold leading-7 tabular-nums";
/** A zero reports quietly and lets a real number carry the weight. */
const QUIET_VALUE_CLASS = "text-lg font-normal leading-7 tabular-nums text-muted-foreground";
const SUB_CLASS = "text-[11px] text-muted-foreground";
const METRIC_CLASS = "flex flex-col gap-0.5 p-1.5 text-left";
const SKELETON_WIDTHS = ["w-20", "w-24", "w-20", "w-16", "w-20"];

/**
 * The metrics sit straight on the page rather than in cards: six bordered boxes
 * for six numbers push the table below the fold on a laptop, and a surface on
 * every metric leaves nothing for the one metric you can click.
 */
const STRIP_CLASS = "-mx-1.5 flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3";

const TONE_CLASS: Record<AdminKpiTone, string> = {
  default: "",
  error: "text-error",
  warning: "text-warning",
};

const ACTIVE_TONE_CLASS: Record<AdminKpiTone, string> = {
  default: "bg-foreground/8 hover:bg-foreground/8",
  error: "bg-error-subtle hover:bg-error-subtle",
  warning: "bg-warning-subtle hover:bg-warning-subtle",
};

function Metric({ metric }: { metric: AdminKpiMetric }) {
  const tone = metric.tone ?? "default";
  const hasValue = metric.value != null;
  const isLoud = hasValue && !metric.isMuted;
  const body = (
    <>
      <span className={ADMIN_LABEL_CLASS}>{metric.label}</span>
      <span className="flex items-baseline gap-1.5">
        <span className={cn(isLoud ? VALUE_CLASS : QUIET_VALUE_CLASS, isLoud && TONE_CLASS[tone])}>
          {hasValue ? metric.value : ADMIN_DASH}
        </span>
        {metric.sub ? <span className={SUB_CLASS}>{metric.sub}</span> : null}
      </span>
    </>
  );

  if (!metric.onFilter) {
    return (
      <div className={METRIC_CLASS} title={metric.title}>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={metric.onFilter}
      aria-pressed={metric.isFilterActive ?? false}
      title={metric.filterTitle ?? metric.title}
      className={cn(
        METRIC_CLASS,
        // The strip sits on --content-background, where bg-muted is invisible;
        // an ink wash is the one hover tint that reads on both themes.
        "cursor-pointer rounded-sm outline-none transition-colors hover:bg-foreground/5",
        "focus-visible:ring-1 focus-visible:ring-ring",
        metric.isFilterActive && ACTIVE_TONE_CLASS[tone],
      )}
    >
      {body}
    </button>
  );
}

/**
 * The numbers that answer "what happened in this window", read left to right,
 * with at most one live gauge pinned to the right of them.
 *
 * Every metric is the same three lines — label, value, sub-label — so a tab
 * that wants a fourth line wants a panel, not a wider strip.
 */
export function AdminKpiStrip({
  metrics,
  live,
  isLoading,
  isStale,
  isError = false,
  onRetry,
}: AdminKpiStripProps) {
  const { t } = useTranslation();

  // Checked ahead of the skeleton: a read that failed is not a read still in
  // flight, and leaving the skeleton up says it is.
  if (isError) {
    return (
      <div className="-mx-1.5 flex shrink-0 flex-wrap items-center gap-3">
        <span className="flex items-center gap-1.5 px-1.5 text-sm text-muted-foreground">
          {/* The icon, not the colour, is what says this failed. */}
          <Icon name="alert-circle" size="xs" colorVar="--destructive" aria-hidden />
          {t("admin.layout.kpi.error", "Could not load these totals.")}
        </span>
        {onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t("admin.layout.retry", "Try again")}
          </Button>
        ) : null}
      </div>
    );
  }

  if (isLoading) {
    return (
      // Mirrors the resolved layout so nothing shifts when the numbers land.
      <div className={STRIP_CLASS}>
        {SKELETON_WIDTHS.map((width) => (
          <Skeleton key={width} className={cn("h-11 rounded-sm bg-foreground/8", width)} />
        ))}
        {live ? <Skeleton className="ml-auto h-11 w-24 rounded-sm bg-foreground/8" /> : null}
      </div>
    );
  }

  return (
    <div
      className={cn(
        STRIP_CLASS,
        "[&>*]:transition-opacity [&>*]:duration-200",
        isStale && "[&>*]:opacity-40",
      )}
      aria-busy={isStale || undefined}
    >
      {metrics.map((metric) => (
        <Metric key={metric.id} metric={metric} />
      ))}
      {live ? (
        <div className="ml-auto">
          <Metric metric={live} />
        </div>
      ) : null}
    </div>
  );
}
